import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import styles from "../designs/HomePage.module.css";
import homeBackground from "../designs/grach-night-bg-v3.png";
import {useUser} from "./UserContext";
import axios from "axios";
import {isDemoModeEnabled} from "../utils/demoAccess";

const formatUkrainianPlural = (count, forms) => {
    const n = Math.abs(Number(count) || 0);
    const mod10 = n % 10;
    const mod100 = n % 100;

    if (mod10 === 1 && mod100 !== 11) return `${count} ${forms[0]}`;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} ${forms[1]}`;
    return `${count} ${forms[2]}`;
};

function HomePage() {
    const navigate = useNavigate();
    const {user, setUser} = useUser();
    const [loading, setLoading] = useState(true);
    const [visualReady, setVisualReady] = useState(() => {
        try {
            return sessionStorage.getItem("grachHomeVisualReady") === "1";
        } catch {
            return false;
        }
    });
    const [error, setError] = useState(null);
    const [homeStats, setHomeStats] = useState({todayMatches: 0, liveMatches: 0});
    const [featuredFocus, setFeaturedFocus] = useState(null);
    const [featuredFocusRefreshing, setFeaturedFocusRefreshing] = useState(false);
    const [isDemoMode, setIsDemoMode] = useState(() => isDemoModeEnabled());
    const [dailyBonus, setDailyBonus] = useState(null);
    const [dailyBonusLoading, setDailyBonusLoading] = useState(false);
    const demoActive = isDemoMode && !user?.has_access;

    useEffect(() => {
        if (visualReady) return;

        let cancelled = false;
        let revealTimer;
        let safetyTimer;
        let revealScheduled = false;
        const startedAt = performance.now();
        const image = new Image();

        const revealHome = () => {
            if (cancelled || revealScheduled) return;
            revealScheduled = true;
            window.clearTimeout(safetyTimer);

            const elapsed = performance.now() - startedAt;
            const remainingDelay = Math.max(0, 650 - elapsed);

            revealTimer = window.setTimeout(() => {
                if (cancelled) return;
                try {
                    sessionStorage.setItem("grachHomeVisualReady", "1");
                } catch {
                }
                setVisualReady(true);
            }, remainingDelay);
        };

        image.onload = revealHome;
        image.onerror = revealHome;
        image.src = homeBackground;

        if (image.complete) revealHome();

        safetyTimer = window.setTimeout(revealHome, 3000);

        return () => {
            cancelled = true;
            window.clearTimeout(revealTimer);
            window.clearTimeout(safetyTimer);
            image.onload = null;
            image.onerror = null;
        };
    }, [visualReady]);

    const todayLabel = useMemo(() => {
        const d = new Date();
        return d.toLocaleDateString("uk-UA", {
            day: "numeric",
            month: "long",
        });
    }, []);

    const todayApiDate = useMemo(() => {
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }, []);

    const applyFeaturedFocusData = useCallback((data) => {
        const isVisible = data?.visible !== false;
        const subtitle = String(data?.subtitle || data?.text || '').trim();

        if (!isVisible || !subtitle) {
            setFeaturedFocus(null);
            return;
        }

        setFeaturedFocus({
            title: data?.title || "Ризик дня",
            subtitle,
            action: data?.action || "Відкрити матч",
            matchName: data?.match_name || "",
            fixtureId: data?.fixture_id || "",
            homeTeam: data?.home_team || "",
            awayTeam: data?.away_team || "",
            date: data?.date || todayApiDate,
            leagueId: data?.league_id || "",
            country: data?.country || "",
            homeLogo: data?.home_logo || "",
            awayLogo: data?.away_logo || "",
            status: data?.status || "NS",
            score: data?.score || "",
            time: data?.kickoff_time || "",
            bet: data?.bet || "",
            odds: data?.odds || "",
        });
    }, [todayApiDate]);

    useEffect(() => {
        if (!user?.telegram_id || (!user?.has_access && !demoActive)) return;

        const loadFeaturedFocus = async () => {
            try {
                const API_BASE = process.env.REACT_APP_API_BASE || '/api';
                const res = await axios.get(`${API_BASE}/home-focus?date=${todayApiDate}&_=${Date.now()}`, {
                    headers: {
                        "Cache-Control": "no-cache, no-store, must-revalidate",
                        "Pragma": "no-cache",
                        "Expires": "0",
                    },
                });

                applyFeaturedFocusData(res.data);
            } catch (err) {
                console.error("Home focus error:", err?.response ? err.response.data : err.message);
                setFeaturedFocus(null);
            }
        };

        loadFeaturedFocus();
    }, [user, todayApiDate, demoActive, applyFeaturedFocusData]);

    const clearCache = () => {
        sessionStorage.removeItem("telegramUser");
        localStorage.removeItem("telegramUser");
    };

    useEffect(() => {
            const initializeUser = async () => {
            setLoading(true);
            setError(null);
            clearCache();
            const demoMode = isDemoModeEnabled();
            setIsDemoMode(demoMode);

            const cachedUser = sessionStorage.getItem("telegramUser") || localStorage.getItem("telegramUser");
            if (cachedUser) {
                try {
                    const parsedUser = JSON.parse(cachedUser);
                    if (parsedUser.telegram_id) {
                        setUser(parsedUser);
                        if (!parsedUser.has_access && !demoMode) {
                            navigate("/access-request");
                        }
                        setLoading(false);
                        return;
                    }
                } catch (e) {
                    clearCache(); // Очищаємо кеш, якщо дані невалідні
                }
            }

            const tg = window.Telegram?.WebApp;
            if (!tg) {
                setError("Не вдалося знайти Telegram WebApp. Відкрийте додаток у Telegram.");
                setLoading(false);
                return;
            }

            tg.ready();
            const initData = tg.initData;
            if (!initData) {
                setError("Не вдалося отримати initData від Telegram.");
                setLoading(false);
                return;
            }

            try {
                const API_BASE = process.env.REACT_APP_API_BASE || '/api';
                const response = await axios.post(`${API_BASE}/init-user`, {}, // тепер payload порожній
                    {
                        headers: {
                            "X-Telegram-Init-Data": initData, "Cache-Control": "no-cache, no-store, must-revalidate",
                        },
                    });

                if (response.data.user?.telegram_id) {
                    setUser(response.data.user);
                    sessionStorage.setItem("telegramUser", JSON.stringify(response.data.user));
                    localStorage.setItem("telegramUser", JSON.stringify(response.data.user));

                    if (!response.data.user.has_access && !demoMode) {
                        navigate("/access-request");
                        return;
                    }
                    setLoading(false);
                } else {
                    throw new Error("Сервер повернув некоректні дані користувача");
                }
            } catch (err) {
                console.error("InitUser error:", err);
                setError("Помилка сервера. Спробуйте ще раз або відкрийте WebApp у Telegram.");
                setLoading(false);
            }
        };

        initializeUser();
    }, [setUser, navigate]);

    useEffect(() => {
        if (!user?.telegram_id || (!user?.has_access && !demoActive)) return;

        const loadHomeStats = async () => {
            try {
                const API_BASE = process.env.REACT_APP_API_BASE || '/api';
                const res = await axios.get(`${API_BASE}/get_matches?date=${todayApiDate}&_=${Date.now()}`, {
                    headers: {
                        "Cache-Control": "no-cache, no-store, must-revalidate",
                        "Pragma": "no-cache",
                        "Expires": "0",
                    },
                });

                const matches = Array.isArray(res.data?.matches) ? res.data.matches : [];
                const liveStatuses = new Set(["LIVE", "1H", "2H", "HT"]);

                const liveMatches = matches.filter((m) => {
                    const status = String(m.status || '').toUpperCase();
                    return liveStatuses.has(status);
                }).length;

                setHomeStats({
                    todayMatches: matches.length,
                    liveMatches,
                });
            } catch (err) {
                console.error("Home stats error:", err?.response ? err.response.data : err.message);
                setHomeStats({todayMatches: 0, liveMatches: 0});
            }
        };

        loadHomeStats();
    }, [user, todayApiDate, demoActive]);

    useEffect(() => {
        if (!user?.telegram_id || demoActive || !user?.has_access) return;

        const loadDailyBonus = async () => {
            try {
                const API_BASE = process.env.REACT_APP_API_BASE || '/api';
                const res = await axios.get(`${API_BASE}/user/${user.telegram_id}/daily-bonus?_=${Date.now()}`, {
                    headers: {
                        "Cache-Control": "no-cache, no-store, must-revalidate",
                        "Pragma": "no-cache",
                        "Expires": "0",
                    },
                });
                setDailyBonus(res.data);
            } catch (err) {
                console.error("Daily bonus status error:", err?.response ? err.response.data : err.message);
                setDailyBonus(null);
            }
        };

        loadDailyBonus();
    }, [user, demoActive]);

    const claimDailyBonus = async () => {
        if (!user?.telegram_id || dailyBonusLoading || dailyBonus?.claimed) return;
        try {
            setDailyBonusLoading(true);
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const res = await axios.post(`${API_BASE}/user/${user.telegram_id}/daily-bonus/claim`);
            setDailyBonus((prev) => ({
                ...(prev || {}),
                ...res.data,
                claimed: true,
                points: res.data.points || prev?.points || 0,
            }));
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.("success");
        } catch (err) {
            console.error("Daily bonus claim error:", err?.response ? err.response.data : err.message);
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.("error");
        } finally {
            setDailyBonusLoading(false);
        }
    };

    const handleRetry = () => {
        clearCache();
        window.location.reload();
    };

    const regenerateFeaturedFocus = async (event) => {
        event?.stopPropagation();
        if (featuredFocusRefreshing || user?.role !== "admin") return;

        try {
            setFeaturedFocusRefreshing(true);
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const initData = window.Telegram?.WebApp?.initData || "";
            const res = await axios.post(
                `${API_BASE}/home-focus/regenerate?date=${todayApiDate}&_=${Date.now()}`,
                {},
                {
                    headers: {
                        "X-Telegram-Init-Data": initData,
                        "Cache-Control": "no-cache, no-store, must-revalidate",
                        "Pragma": "no-cache",
                        "Expires": "0",
                    },
                }
            );
            applyFeaturedFocusData(res.data);
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.("success");
        } catch (err) {
            console.error("Home focus regenerate error:", err?.response ? err.response.data : err.message);
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.("error");
        } finally {
            setFeaturedFocusRefreshing(false);
        }
    };

    const openFeaturedMatch = () => {
        if (!featuredFocus?.homeTeam || !featuredFocus?.awayTeam) {
            navigate("/football");
            return;
        }

        const qs = new URLSearchParams({
            home_team: featuredFocus.homeTeam,
            away_team: featuredFocus.awayTeam,
            date: featuredFocus.date || todayApiDate,
            country: featuredFocus.country || "UNKNOWN",
            home_logo: featuredFocus.homeLogo || "",
            away_logo: featuredFocus.awayLogo || "",
            status: featuredFocus.status || "NS",
            score: featuredFocus.score || "",
            elapsed: "",
            time: featuredFocus.time || "",
        });
        if (featuredFocus.fixtureId) qs.set("fixture_id", String(featuredFocus.fixtureId));
        if (featuredFocus.leagueId) qs.set("league_id", featuredFocus.leagueId);

        localStorage.setItem("selectedDate", featuredFocus.date || todayApiDate);
        navigate(`/match?${qs.toString()}`);
    };

    if (loading || !visualReady) return (
        <div className={styles.splashScreen} role="status" aria-live="polite">
            <div className={styles.splashGlow}/>
            <div className={styles.splashBrand}>
                <span className={styles.splashCrown} aria-hidden="true">♔</span>
                <span className={styles.splashFamily}>СІМ’Я</span>
                <strong className={styles.splashName}>ГРАЧА</strong>
            </div>
            <div className={styles.splashProgress} aria-hidden="true">
                <span/>
            </div>
            <span className={styles.splashLabel}>Готуємо твою гру</span>
        </div>
    );
    if (error) return (<div>
        <p>{error}</p>
        <button onClick={handleRetry}>Спробувати ще раз</button>

        {/* DEBUG: щоб бачити реальні дані Telegram */}
        <pre style={{fontSize: "12px", whiteSpace: "pre-wrap"}}>
                    {JSON.stringify(window.Telegram?.WebApp?.initDataUnsafe, null, 2)}
                </pre>
    </div>);

    return (<div className={styles.mainMenuContainer}>
        <div className={styles.mainContent}>
            <div className={styles.headerArea}>
                <div className={styles.brandBlock}>
                    <span className={styles.brandCrown} aria-hidden="true">♔</span>
                    <h1 className={styles.menuTitle}>
                        <span>СІМ’Я</span>
                        <strong>ГРАЧА</strong>
                    </h1>
                    <span className={styles.brandTagline}>Твоя гра. Твій вибір.</span>
                </div>
                {user && (
                    <>
                        <div className={styles.welcomeCard}>
                            <div className={styles.welcomeIcon}>G</div>
                            <div className={styles.welcomeText}>
                                <span className={styles.welcomeLabel}>Радий бачити,</span>
                                <span className={styles.welcomeName}>
                                    {user.first_name || user.username || "друже"}
                                </span>
                            </div>
                        </div>

                        {!user.has_access && (
                            <button
                                type="button"
                                className={styles.accessUpgradeCard}
                                onClick={() => navigate("/access-request")}
                            >
                                <div className={styles.accessUpgradeText}>
                                    <span>Демо-режим</span>
                                    <strong>Отримати повний доступ</strong>
                                </div>
                                <div className={styles.accessUpgradeArrow}>→</div>
                            </button>
                        )}

                        <div className={styles.homeStatsCard}>
                            <div className={styles.homeStatsHeader}>
                                <span className={styles.homeStatsTitle}>
                                    <span className={styles.sectionMarker}/>
                                    Матчі сьогодні
                                </span>
                                <span className={styles.homeStatsDate}>{todayLabel}</span>
                            </div>

                            <div className={styles.homeStatsBadges}>
                                <div className={`${styles.homeStatsBadge} ${styles.matches}`}>
                                    <span className={styles.homeStatsBadgeIcon}>⚽</span>
                                    <span className={styles.homeStatsBadgeText}>
                                        {formatUkrainianPlural(homeStats.todayMatches, ["матч", "матчі", "матчів"])}
                                    </span>
                                </div>
                                <div className={`${styles.homeStatsBadge} ${styles.live}`}>
                                    <span className={styles.homeStatsBadgeIcon}>🔥</span>
                                    <span className={styles.homeStatsBadgeText}>
                                        {formatUkrainianPlural(homeStats.liveMatches, ["live-матч", "live-матчі", "live-матчів"])}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {!demoActive && user.has_access && dailyBonus && (
                            <button
                                type="button"
                                className={`${styles.dailyBonusCard} ${dailyBonus.claimed ? styles.dailyBonusClaimed : ''}`}
                                onClick={claimDailyBonus}
                                disabled={dailyBonusLoading || dailyBonus.claimed}
                            >
                                <div className={styles.dailyBonusText}>
                                    <span>{dailyBonus.claimed ? "Бонус забрано" : "Щоденний бонус"}</span>
                                    <strong>{dailyBonus.claimed ? `Баланс: ${dailyBonus.balance || 0}` : `Забрати +${dailyBonus.points || 0} балів`}</strong>
                                </div>
                                <div className={styles.dailyBonusIcon}>{dailyBonus.claimed ? "✓" : "+"}</div>
                            </button>
                        )}

                        {featuredFocus && (
                            <div
                                role="button"
                                tabIndex={0}
                                className={`${styles.featuredCard} ${demoActive ? styles.featuredCardLocked : ''}`}
                                onClick={openFeaturedMatch}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        openFeaturedMatch();
                                    }
                                }}
                            >
                                <div className={styles.featuredCardGlow}/>
                                <div className={styles.featuredCardContent}>
                                    <div className={styles.featuredCardTopRow}>
                                        <div className={styles.featuredCardHeading}>
                                            <span className={styles.featuredCardEmoji}>🔥</span>
                                            <span className={styles.featuredCardTitle}>{featuredFocus.title}</span>
                                        </div>
                                        {user?.role === "admin" && (
                                            <button
                                                type="button"
                                                className={styles.featuredRegenerateButton}
                                                onClick={regenerateFeaturedFocus}
                                                onKeyDown={(event) => event.stopPropagation()}
                                                disabled={featuredFocusRefreshing}
                                            >
                                                {featuredFocusRefreshing ? "Генерується..." : "Перегенерувати"}
                                            </button>
                                        )}
                                    </div>
                                    {(featuredFocus.matchName || featuredFocus.bet || featuredFocus.odds) && (
                                        <div className={`${styles.featuredCardPick} ${demoActive ? styles.blurredText : ''}`}>
                                            {featuredFocus.matchName && <span>{featuredFocus.matchName}</span>}
                                            {featuredFocus.bet && <strong>{featuredFocus.bet}</strong>}
                                            {featuredFocus.odds && <em>кеф {featuredFocus.odds}</em>}
                                        </div>
                                    )}
                                    <div className={`${styles.featuredCardSubtitle} ${demoActive ? styles.blurredText : ''}`}>
                                        {featuredFocus.subtitle}
                                    </div>
                                    <div className={styles.featuredCardFooterRow}>
                                        <div className={styles.featuredCardAction}>{featuredFocus.action}</div>
                                        <div className={styles.featuredCardAuthor}>- Грач</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className={styles.menuButtons}>
                <button
                    className={`${styles.menuButton} ${homeStats.liveMatches > 0 ? styles.liveActive : ''}`}
                    onClick={() => navigate("/football")}
                >
                    <span>Матчі</span><span className={styles.menuArrow}>→</span>
                </button>

                <button
                    className={`${styles.menuButton} ${demoActive ? styles.lockedMenuButton : ''}`}
                    onClick={() => !demoActive && navigate("/history")}
                    disabled={demoActive}
                >
                    <span>Історія</span><span className={styles.menuArrow}>{demoActive ? '🔒' : '→'}</span>
                </button>

                <button
                    className={`${styles.menuButton} ${demoActive ? styles.lockedMenuButton : ''}`}
                    onClick={() => !demoActive && navigate("/profile")}
                    disabled={demoActive}
                >
                    <span>Профіль</span><span className={styles.menuArrow}>{demoActive ? '🔒' : '→'}</span>
                </button>

                <button
                    className={`${styles.menuButton} ${demoActive ? styles.lockedMenuButton : ''}`}
                    onClick={() => !demoActive && navigate("/shop")}
                    disabled={demoActive}
                >
                    <span>Магазин</span><span className={styles.menuArrow}>{demoActive ? '🔒' : '→'}</span>
                </button>

                {user?.role === "admin" && (
                    <button
                        className={styles.menuButton}
                        onClick={() => navigate("/admin")}
                    >
                        <span>Адмінка</span><span className={styles.menuArrow}>→</span>
                    </button>
                )}

                {/*<button*/}
                {/*    className={styles.menuButton}*/}
                {/*    onClick={() => navigate("/")}*/}
                {/*    style={{*/}
                {/*        "--snow-scale-x": 2.95,*/}
                {/*        "--snow-offset-y": "-7px",*/}
                {/*    }}*/}
                {/*>*/}
                {/*    Налаштування*/}
                {/*</button>*/}
            </div>
        </div>

    </div>);

}

export default HomePage;
